import { useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import BadgeDownload from "@/components/BadgeDownload";
import { Mail, Twitter, Facebook, Linkedin, MessageCircle, ExternalLink, Check, Code2, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * A one-stop panel for hosts. No copy-paste — every action is a single click:
 * - Email me the full marketing kit
 * - Post directly to X / Facebook / LinkedIn / WhatsApp
 * - Download the badge (PNG / SVG)
 * - Open my public certification page
 */
export default function HostMarketingKit({ property }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!property?.certification_code) return null;

  const code = property.certification_code;
  const tier = property.certification_tier || "standard";
  const publicUrl = `${window.location.origin}/certification/${code}`;
  const score = property.freshness_score || 0;
  const caption = `Booking with confidence — ${property.name} is Guest Smell Certified™ with a ${score}/100 freshness score. See it. Smell it. Book it.`;

  // Embeddable trust badge — every impression on a host's Airbnb/website/socials
  // deep-links back to their public certification page. Free viral loop.
  const badgeUrl = `${process.env.REACT_APP_BACKEND_URL}/api/badge/${code}.svg`;
  const htmlSnippet = `<a href="${publicUrl}" target="_blank" rel="noopener"><img src="${badgeUrl}" alt="Guest Smell Certified — ${property.name}" width="320" height="140"/></a>`;
  const mdSnippet = `[![Guest Smell Certified — ${property.name}](${badgeUrl})](${publicUrl})`;

  const copyTo = async (val, label) => {
    try { await navigator.clipboard.writeText(val); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — long-press the text to copy manually"); }
  };

  const emailKit = async () => {
    setSending(true);
    try {
      await api.post("/hosts/marketing-kit", { property_id: property.id, origin_url: window.location.origin });
      setSent(true);
      toast.success("Marketing kit sent to your email");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSending(false); }
  };

  const open = (u, channel) => {
    api.post("/analytics/event", { property_id: property.id, event_type: "share", channel }).catch(() => {});
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=640");
  };
  const encoded = encodeURIComponent(caption);
  const url = encodeURIComponent(publicUrl);

  return (
    <div className="space-y-6" data-testid="host-marketing-kit">
      {/* Badge */}
      <div className="flex justify-center">
        <BadgeDownload property={property} tier={tier} code={code} size={200} />
      </div>

      {/* One-click Email me the kit */}
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">One-click delivery</div>
        <Button
          onClick={emailKit}
          disabled={sending || sent}
          data-testid="kit-email-me"
          className="mt-2 w-full h-11 rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white"
        >
          {sent ? (<><Check className="w-4 h-4 mr-1" /> Sent to your inbox</>) :
            sending ? "Sending…" :
            (<><Mail className="w-4 h-4 mr-1" /> Email me my kit</>)}
        </Button>
        <p className="mt-2 text-xs text-slate-500">
          Includes: badge download link, ready-to-post caption, public certification URL, and direct social buttons.
        </p>
      </div>

      {/* Direct social posting */}
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Post now (no copy-paste)</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => open(`https://twitter.com/intent/tweet?text=${encoded}&url=${url}`, "twitter")} data-testid="kit-share-twitter"
            className="flex items-center justify-center gap-2 h-11 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors">
            <Twitter className="w-4 h-4" /> Post on X
          </button>
          <button onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "facebook")} data-testid="kit-share-facebook"
            className="flex items-center justify-center gap-2 h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
            <Facebook className="w-4 h-4" /> Facebook
          </button>
          <button onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "linkedin")} data-testid="kit-share-linkedin"
            className="flex items-center justify-center gap-2 h-11 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium transition-colors">
            <Linkedin className="w-4 h-4" /> LinkedIn
          </button>
          <button onClick={() => open(`https://api.whatsapp.com/send?text=${encoded}%20${url}`, "whatsapp")} data-testid="kit-share-whatsapp"
            className="flex items-center justify-center gap-2 h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
        </div>
      </div>

      {/* Embed anywhere — badge that other sites can host */}
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold flex items-center gap-1.5">
          <Code2 className="w-3 h-3" /> Embed on any site
        </div>
        <div className="mt-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
          {/* Live SVG preview served straight from the backend endpoint */}
          <img
            src={badgeUrl}
            alt={`Guest Smell Certified — ${property.name}`}
            width={320}
            height={140}
            className="mx-auto rounded-xl shadow-sm block"
            data-testid="embed-badge-preview"
          />
          <p className="mt-3 text-xs text-slate-500 text-center leading-relaxed">
            Paste this on your Airbnb listing description, personal site, or Instagram bio linktree. Every view links back to your certification page.
          </p>
          <div className="mt-4 space-y-2">
            {[
              { label: "Image URL", value: badgeUrl, testid: "embed-copy-url" },
              { label: "HTML (clickable)", value: htmlSnippet, testid: "embed-copy-html" },
              { label: "Markdown", value: mdSnippet, testid: "embed-copy-md" },
            ].map((row) => (
              <button
                key={row.label}
                onClick={() => copyTo(row.value, row.label)}
                data-testid={row.testid}
                className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">{row.label}</div>
                  <div className="text-xs text-slate-700 truncate font-mono">{row.value}</div>
                </div>
                <Copy className="w-4 h-4 text-emerald-600 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Public link */}
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-slate-500 font-semibold">Public certification page</div>
        <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="kit-open-public"
          className="mt-2 flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
          <span className="text-sm text-[#0A1128] truncate">/certification/{code}</span>
          <ExternalLink className="w-4 h-4 text-slate-400" />
        </a>
      </div>
    </div>
  );
}
